import {
    useCallback,
    useEffect,
    useState,
    useMemo
} from "react";
import {
    type GestureResponderEvent,
    type NativeSyntheticEvent,
    type PressableProps,
    type TargetedEvent,
    type MouseEvent,
    Platform
} from "react-native";

export type InteractionStateName = "focus-visible" | "disabled" | "pressed" | "hover" | "rest";

export type InteractionDataSetType = {
    ncoreState: string;
};

export type InteractionHandlersType = {
    onFocus: (event: NativeSyntheticEvent<TargetedEvent>) => void;
    onBlur: (event: NativeSyntheticEvent<TargetedEvent>) => void;
    onPressOut: (event: GestureResponderEvent) => void;
    onPressIn: (event: GestureResponderEvent) => void;
    onHoverOut: (event: MouseEvent) => void;
    onHoverIn: (event: MouseEvent) => void;
    dataSet?: InteractionDataSetType;
};

export type InteractionStateConstantType = {
    onPressOut?: PressableProps["onPressOut"];
    onHoverOut?: PressableProps["onHoverOut"];
    onPressIn?: PressableProps["onPressIn"];
    onHoverIn?: PressableProps["onHoverIn"];
    onFocus?: PressableProps["onFocus"];
    isDescendantFocusIgnored?: boolean;
    onBlur?: PressableProps["onBlur"];
    isDisabled?: boolean;
};

export type InteractionStateNameConstantType = {
    isFocusVisible: boolean;
    isDisabled: boolean;
    isHovered: boolean;
    isPressed: boolean;
};

export type InteractionStateType = {
    interactionHandlers: InteractionHandlersType;
    isFocusVisible: boolean;
    isHovered: boolean;
    isPressed: boolean;
    isFocused: boolean;
};

type FocusTargetType = {
    matches: (selector: string) => boolean;
};

export const INTERACTION_STATE_SELECTOR = "[data-ncore-state]";

export const INTERACTION_STATE_DATA_KEY = "ncoreState";

const isFocusTarget = (target: unknown): target is FocusTargetType => {
    return typeof target === "object" && target !== null && "matches" in target && typeof target.matches === "function";
};

const getIsFocusVisible = (target: unknown): boolean => {
    if(Platform.OS !== "web" || !isFocusTarget(target)) {
        return true;
    }

    try {
        return target.matches(":focus-visible");
    } catch {
        return true;
    }
};

export const getInteractionStateName = ({
    isFocusVisible,
    isDisabled,
    isHovered,
    isPressed
}: InteractionStateNameConstantType): string => {
    if(isDisabled) {
        return "disabled";
    }

    const stateNames: Array<InteractionStateName> = [];

    if(isHovered) {
        stateNames.push("hover");
    }

    if(isPressed) {
        stateNames.push("pressed");
    }

    if(isFocusVisible) {
        stateNames.push("focus-visible");
    }

    if(!stateNames.length) {
        return "rest";
    }

    return stateNames.join(" ");
};

const useInteractionState = ({
    isDescendantFocusIgnored = false,
    onHoverOut: onHoverOutProp,
    onPressOut: onPressOutProp,
    onHoverIn: onHoverInProp,
    onPressIn: onPressInProp,
    onFocus: onFocusProp,
    isDisabled = false,
    onBlur: onBlurProp
}: InteractionStateConstantType = {}): InteractionStateType => {
    const [
        isFocusVisibleState,
        setIsFocusVisibleState
    ] = useState(false);

    const [
        isHoveredState,
        setIsHoveredState
    ] = useState(false);

    const [
        isPressedState,
        setIsPressedState
    ] = useState(false);

    const [
        isFocusedState,
        setIsFocusedState
    ] = useState(false);

    useEffect(() => {
        if(!isDisabled) {
            return;
        }

        setIsFocusVisibleState(false);

        setIsPressedState(false);

        setIsFocusedState(false);
    }, [
        isDisabled
    ]);

    const onPressIn = useCallback((event: GestureResponderEvent) => {
        if(!isDisabled) {
            setIsPressedState(true);
        }

        onPressInProp?.(event);
    }, [
        isDisabled,
        onPressInProp
    ]);

    const onPressOut = useCallback((event: GestureResponderEvent) => {
        setIsPressedState(false);

        onPressOutProp?.(event);
    }, [
        onPressOutProp
    ]);

    const onHoverIn = useCallback((event: MouseEvent) => {
        if(!isDisabled) {
            setIsHoveredState(true);
        }

        onHoverInProp?.(event);
    }, [
        isDisabled,
        onHoverInProp
    ]);

    const onHoverOut = useCallback((event: MouseEvent) => {
        setIsHoveredState(false);

        onHoverOutProp?.(event);
    }, [
        onHoverOutProp
    ]);

    const onFocus = useCallback((event: NativeSyntheticEvent<TargetedEvent>) => {
        if(isDescendantFocusIgnored && Platform.OS === "web" && event.target !== event.currentTarget) {
            onFocusProp?.(event);

            return;
        }

        if(!isDisabled) {
            setIsFocusVisibleState(getIsFocusVisible(event.nativeEvent.target));

            setIsFocusedState(true);
        }

        onFocusProp?.(event);
    }, [
        isDescendantFocusIgnored,
        isDisabled,
        onFocusProp
    ]);

    const onBlur = useCallback((event: NativeSyntheticEvent<TargetedEvent>) => {
        setIsFocusVisibleState(false);

        setIsFocusedState(false);

        onBlurProp?.(event);
    }, [
        onBlurProp
    ]);

    const isFocusVisible = !isDisabled && isFocusVisibleState;

    const isHovered = !isDisabled && isHoveredState;

    const isPressed = !isDisabled && isPressedState;

    const isFocused = !isDisabled && isFocusedState;

    const interactionStateName = getInteractionStateName({
        isFocusVisible,
        isDisabled,
        isHovered,
        isPressed
    });

    const interactionHandlers = useMemo((): InteractionHandlersType => {
        const handlers: InteractionHandlersType = {
            onHoverOut,
            onPressOut,
            onHoverIn,
            onPressIn,
            onFocus,
            onBlur
        };

        if(Platform.OS !== "web") {
            return handlers;
        }

        return {
            ...handlers,
            dataSet: {
                [INTERACTION_STATE_DATA_KEY]: interactionStateName
            }
        };
    }, [
        interactionStateName,
        onPressOut,
        onHoverOut,
        onPressIn,
        onHoverIn,
        onFocus,
        onBlur
    ]);

    return {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed,
        isFocused
    };
};
export default useInteractionState;
